import * as React from "react";
import {EventSuppressionRules} from "../../../../../generated/api";
import {Accordion, Button, Checkbox, Form, Input} from "semantic-ui-react";
import AceEditor from "react-ace";

import 'brace/mode/groovy'
import 'brace/ext/searchbox'

interface SuppressionRulesMaskProps {
    localRules: EventSuppressionRules;
    setLocalRules: React.Dispatch<React.SetStateAction<EventSuppressionRules>>;
    activeAccordions: { eventTypes: boolean; transportOrderEvents: boolean };
    setActiveAccordions: React.Dispatch<React.SetStateAction<{
        eventTypes: boolean;
        transportOrderEvents: boolean
    }>>;
    onChange: (r: EventSuppressionRules) => void;
}

export function SuppressionRulesMask(props: SuppressionRulesMaskProps) {
    const {
        localRules,
        setLocalRules,
        activeAccordions,
        setActiveAccordions,
        onChange
    } = props;

    const [suppressionScript, setSuppressionScript] = React.useState(localRules.suppressionScript || "");
    const pendingScriptSyncRef = React.useRef<number | null>(null);
    const lastSyncedScriptRef = React.useRef(localRules.suppressionScript || "");

    function update<K extends keyof EventSuppressionRules>(key: K, value: EventSuppressionRules[K]) {
        const updated = {...localRules, [key]: value};
        setLocalRules(updated);
        onChange(updated);
    }

    function flushSuppressionScript(nextValue?: string) {
        const valueToPersist = nextValue ?? suppressionScript;
        if (valueToPersist !== (localRules.suppressionScript || "")) {
            update("suppressionScript", valueToPersist);
        }
        lastSyncedScriptRef.current = valueToPersist;
    }

    React.useEffect(() => {
        const externalValue = localRules.suppressionScript || "";
        if (externalValue !== lastSyncedScriptRef.current) {
            setSuppressionScript(externalValue);
            lastSyncedScriptRef.current = externalValue;
        }
    }, [localRules.suppressionScript]);

    React.useEffect(() => {
        return () => {
            if (pendingScriptSyncRef.current != null) {
                clearTimeout(pendingScriptSyncRef.current);
            }
        };
    }, []);

    function onSuppressionScriptChange(value: string) {
        setSuppressionScript(value);
        if (pendingScriptSyncRef.current != null) {
            clearTimeout(pendingScriptSyncRef.current);
        }
        pendingScriptSyncRef.current = window.setTimeout(() => {
            flushSuppressionScript(value);
            pendingScriptSyncRef.current = null;
        }, 300);
    }

    // Enum-Optionen
    const suppressEventTypesOptions = Object.values(EventSuppressionRules.SuppressEventTypesEnum);
    const suppressTransportOrderEventsOptions = Object.values(EventSuppressionRules.SuppressTransportOrderEventsEnum);

    // Checkbox-Handler für Enum-Arrays
    function toggleEnumArrayValue<K extends keyof EventSuppressionRules>(key: K, value: string, checked: boolean) {
        const arr = (localRules[key] as string[] ?? []);
        if (checked) {
            update(key, [...arr, value] as any);
        } else {
            update(key, arr.filter(v => v !== value) as any);
        }
    }

    return (
        <Form>
            {/* suppressEventTypes als Checkbox-Liste */}
            <Accordion
                fluid
                styled
                panels={[
                    {
                        key: "eventTypes",
                        title: "Suppress Event Types",
                        content: {
                            content: (
                                <Form.Group widths="equal" style={{flexWrap: "wrap"}}>
                                    {suppressEventTypesOptions.map(v => (
                                        <Form.Field key={v} style={{minWidth: 200, marginBottom: 8}}>
                                            <Checkbox
                                                label={v}
                                                checked={localRules.suppressEventTypes?.includes(v) ?? false}
                                                onChange={(_, d) => toggleEnumArrayValue("suppressEventTypes", v, d.checked)}
                                            />
                                        </Form.Field>
                                    ))}
                                </Form.Group>
                            )
                        }
                    }
                ]}
                activeIndex={activeAccordions.eventTypes ? 0 : -1}
                onTitleClick={() =>
                    setActiveAccordions(a => ({...a, eventTypes: !a.eventTypes}))
                }
            />
            {/* suppressEventsFromCompaniesWithIds bleibt als dynamische Input-Liste */}
            <Form.Field>
                <h4>Suppress Events From Companies With IDs</h4>
                {(localRules.suppressEventsFromCompaniesWithIds || []).map((v, idx) => (
                    <Form.Group key={idx}>
                        <Input
                            value={v}
                            onChange={(_, d) => {
                                const arr = localRules.suppressEventsFromCompaniesWithIds ?? [];
                                const updated = arr.map((val, i) => i === idx ? d.value : val);
                                update("suppressEventsFromCompaniesWithIds", updated);
                            }}
                            placeholder="Company ID"
                        />
                        <Button icon="trash" color="red"
                                onClick={() => {
                                    const arr = localRules.suppressEventsFromCompaniesWithIds ?? [];
                                    update("suppressEventsFromCompaniesWithIds", arr.filter((_, i) => i !== idx));
                                }}
                        />
                    </Form.Group>
                ))}
                <Button icon="plus" color="green" onClick={() => {
                    const arr = localRules.suppressEventsFromCompaniesWithIds ?? [];
                    update("suppressEventsFromCompaniesWithIds", [...arr, ""]);
                }} content="Hinzufügen"/>
            </Form.Field>
            {/* suppressTransportOrderEvents als Checkbox-Liste */}
            <Accordion
                fluid
                styled
                panels={[
                    {
                        key: "transportOrderEvents",
                        title: "Suppress Transport Order Events",
                        content: {
                            content: (
                                <Form.Group widths="equal" style={{flexWrap: "wrap"}}>
                                    {suppressTransportOrderEventsOptions.map(v => (
                                        <Form.Field key={v} style={{minWidth: 200, marginBottom: 8}}>
                                            <Checkbox
                                                label={v}
                                                checked={localRules.suppressTransportOrderEvents?.includes(v) ?? false}
                                                onChange={(_, d) => toggleEnumArrayValue("suppressTransportOrderEvents", v, d.checked)}
                                            />
                                        </Form.Field>
                                    ))}
                                </Form.Group>
                            )
                        }
                    }
                ]}
                activeIndex={activeAccordions.transportOrderEvents ? 0 : -1}
                onTitleClick={() =>
                    setActiveAccordions(a => ({...a, transportOrderEvents: !a.transportOrderEvents}))
                }
            />
            {/* Booleans */}
            <Form.Field>
                <Checkbox
                    label="Suppress Input Missing Events"
                    checked={!!localRules.suppressInputMissingEvents}
                    onChange={(_, d) => update("suppressInputMissingEvents", d.checked)}
                />
            </Form.Field>
            <Form.Field>
                <Checkbox
                    label="Suppress Link Events"
                    checked={!!localRules.suppressLinkEvents}
                    onChange={(_, d) => update("suppressLinkEvents", d.checked)}
                />
            </Form.Field>
            <Form.Field>
                <Checkbox
                    label="Suppress Status Update Observer Events"
                    checked={!!localRules.suppressStatusUpdateObserverEvents}
                    onChange={(_, d) => update("suppressStatusUpdateObserverEvents", d.checked)}
                />
            </Form.Field>
            <Form.Field>
                <Checkbox
                    label="Suppress System Status Notification Events"
                    checked={!!localRules.suppressSystemStatusNotificationEvents}
                    onChange={(_, d) => update("suppressSystemStatusNotificationEvents", d.checked)}
                />
            </Form.Field>
            <Form.Field>
                <Checkbox
                    label="Suppress Update Events That Are Coocurring With Assign Events"
                    checked={!!localRules.suppressUpdateEventsThatAreCoocurringWithAssignEvents}
                    onChange={(_, d) => update("suppressUpdateEventsThatAreCoocurringWithAssignEvents", d.checked)}
                />
            </Form.Field>
            <Form.Field>
                <Checkbox
                    label="Suppress Update Events With Irrelevant Changes For Humans"
                    checked={!!localRules.suppressUpdateEventsWithIrrelevantChangesForHumans}
                    onChange={(_, d) => update("suppressUpdateEventsWithIrrelevantChangesForHumans", d.checked)}
                />
            </Form.Field>
            {/* suppressionScript */}
            <Form.Field>
                <label>Suppression Script</label>
                <AceEditor
                    theme="monokai"
                    mode='groovy'
                    value={suppressionScript}
                    onChange={onSuppressionScriptChange}
                    onBlur={() => flushSuppressionScript()}
                    width="100%"
                    height="550px"
                    setOptions={{useWorker: false}}
                />
            </Form.Field>
        </Form>
    );
}